// src/config/theme.config.js

export const THEME_COLORS = {
  brand: '#f58025',
  darkblue: '#152451',
  violet: '#92278f',
  teal: '#009391',
  yellow: '#ffdd00',
  brightblue: '#0093cf',
  pink: '#ed145b',
  green: '#b0bc22',
  grey: '#dcdeef',
};

export const COLOR_SCHEMES = {
  kpi: ['brand', 'pink', 'darkblue', 'brightblue', 'green', 'teal'],
  performance: ['green', 'brightblue', 'violet', 'brand'],
  team: ['brand', 'brightblue', 'violet', 'teal', 'pink', 'green'],
  offerTypes: ['green', 'teal', 'yellow', 'brightblue', 'pink'],
  charts: ['brand', 'brightblue', 'violet', 'teal', 'pink', 'green', 'yellow', 'darkblue'],
};

// Get hex color value
export const getColorHex = (colorName) => {
  return THEME_COLORS[colorName] || THEME_COLORS.brand;
};

// Get multiple colors for charts
export const getChartColors = (colorNames) => {
  return colorNames.map(name => getColorHex(name));
};

// ============================================
// BUTTON THEME CONFIGURATION
// ============================================

export const BUTTON_THEME = {
  // Base styles applied to all buttons
  base: 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',

  // Size variants
  sizes: {
    xs: 'px-2 py-1 text-xs gap-1',
    sm: 'px-3 py-1.5 text-sm gap-1.5',
    md: 'px-4 py-2 text-sm gap-2',
    lg: 'px-6 py-2.5 text-base gap-2',
    xl: 'px-8 py-3 text-lg gap-3',
  },

  // Icon-only button sizes (square buttons)
  iconSizes: {
    xs: 'p-1',
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-2.5',
    xl: 'p-3',
  },

  // Icon sizes within buttons
  iconClasses: {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-7 h-7',
  },

  // Button variants with full styling
  variants: {
    // Primary - Brand Orange
    primary: {
      default: 'bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700 focus:ring-brand-500 shadow-sm',
      disabled: 'bg-grey-300 text-grey-500',
      loading: 'bg-brand-500 text-white',
    },

    // Secondary - Outlined
    secondary: {
      default: 'bg-white text-darkblue-500 border border-grey-300 hover:bg-grey-50 active:bg-grey-100 focus:ring-brand-500 shadow-sm',
      disabled: 'bg-white text-grey-400 border-grey-200',
      loading: 'bg-white text-darkblue-500 border border-grey-300',
    },

    // Danger - Pink/Red
    danger: {
      default: 'bg-pink-500 text-white hover:bg-pink-600 active:bg-pink-700 focus:ring-pink-500 shadow-sm',
      disabled: 'bg-grey-300 text-grey-500',
      loading: 'bg-pink-500 text-white',
    },

    // Success - Green
    success: {
      default: 'bg-green-500 text-white hover:bg-green-600 active:bg-green-700 focus:ring-green-500 shadow-sm',
      disabled: 'bg-grey-300 text-grey-500',
      loading: 'bg-green-500 text-white',
    },

    // Info - Bright Blue
    info: {
      default: 'bg-brightblue-500 text-white hover:bg-brightblue-600 active:bg-brightblue-700 focus:ring-brightblue-500 shadow-sm',
      disabled: 'bg-grey-300 text-grey-500',
      loading: 'bg-brightblue-500 text-white',
    },

    // Warning - Yellow
    warning: {
      default: 'bg-yellow-500 text-darkblue-500 hover:bg-yellow-600 active:bg-yellow-700 focus:ring-yellow-500 shadow-sm',
      disabled: 'bg-grey-300 text-grey-500',
      loading: 'bg-yellow-500 text-darkblue-500',
    },

    // Ghost - Transparent with hover
    ghost: {
      default: 'bg-transparent text-darkblue-500 hover:bg-grey-100 active:bg-grey-200 focus:ring-brand-500',
      disabled: 'bg-transparent text-grey-400',
      loading: 'bg-transparent text-darkblue-500',
    },

    // Link - Text only
    link: {
      default: 'bg-transparent text-brand-500 hover:text-brand-600 active:text-brand-700 underline-offset-4 hover:underline focus:ring-brand-500',
      disabled: 'bg-transparent text-grey-400',
      loading: 'bg-transparent text-brand-500',
    },

    // Dark - Dark blue
    dark: {
      default: 'bg-darkblue-500 text-white hover:bg-darkblue-600 active:bg-darkblue-700 focus:ring-darkblue-500 shadow-sm',
      disabled: 'bg-grey-300 text-grey-500',
      loading: 'bg-darkblue-500 text-white',
    },

    // Outline Primary - Brand outlined
    outlinePrimary: {
      default: 'bg-transparent text-brand-500 border-2 border-brand-500 hover:bg-brand-50 active:bg-brand-100 focus:ring-brand-500',
      disabled: 'bg-transparent text-grey-400 border-grey-300',
      loading: 'bg-transparent text-brand-500 border-2 border-brand-500',
    },
  },

  // Loading spinner configuration
  spinner: {
    xs: 'w-3 h-3 border-2',
    sm: 'w-4 h-4 border-2',
    md: 'w-5 h-5 border-2',
    lg: 'w-6 h-6 border-2',
    xl: 'w-7 h-7 border-3',
    className: 'border-current border-t-transparent rounded-full animate-spin',
  },
};

// ============================================
// BUTTON HELPER FUNCTIONS
// ============================================

/**
 * Get complete button classes based on variant, size, and state
 */
export const getButtonClasses = ({
  variant = 'primary',
  size = 'md',
  iconOnly = false,
  disabled = false,
  loading = false,
  fullWidth = false,
  className = '',
}) => {
  const variantConfig = BUTTON_THEME.variants[variant] || BUTTON_THEME.variants.primary;
  
  let classes = [BUTTON_THEME.base];

  // Size
  if (iconOnly) {
    classes.push(BUTTON_THEME.iconSizes[size]);
  } else {
    classes.push(BUTTON_THEME.sizes[size]);
  }

  // State-based variant styling
  if (disabled) {
    classes.push(variantConfig.disabled);
  } else if (loading) {
    classes.push(variantConfig.loading);
  } else {
    classes.push(variantConfig.default);
  }

  // Full width
  if (fullWidth) {
    classes.push('w-full');
  }

  // Custom classes
  if (className) {
    classes.push(className);
  }

  return classes.join(' ');
};

/**
 * Get icon classes for buttons
 */
export const getButtonIconClasses = (size = 'md') => {
  return BUTTON_THEME.iconClasses[size];
};

/**
 * Get spinner classes for loading state
 */
export const getButtonSpinnerClasses = (size = 'md') => {
  return `${BUTTON_THEME.spinner[size]} ${BUTTON_THEME.spinner.className}`;
};

// ============================================
// MODAL THEME CONFIGURATION
// ============================================

export const MODAL_THEME = {
  // Header styling
  header: {
    default: 'bg-grey-50 border-b border-grey-200',
    gray: 'bg-grey-100 border-b border-grey-300',
    dark: 'bg-darkblue-500 border-b border-darkblue-600',
    black: 'bg-black border-b border-gray-800',
    gradient: 'bg-gradient-to-r from-brand-500 to-brand-600 text-white border-0',
    
    title: {
      light: 'text-xl font-semibold text-darkblue-500',
      dark: 'text-xl font-semibold text-white',
      gradient: 'text-xl font-semibold text-white',
    },
    subtitle: {
      light: 'text-sm text-grey-600 mt-1',
      dark: 'text-sm text-white/90 mt-1',
      gradient: 'text-sm text-white/90 mt-1',
    },
    closeButton: {
      light: 'text-grey-400 hover:text-grey-600 transition-colors',
      dark: 'text-white/80 hover:text-white transition-colors',
      gradient: 'text-white/80 hover:text-white transition-colors',
    },
  },

  // Body styling
  body: {
    default: 'p-6 overflow-y-auto flex-1',
    padding: 'p-6',
    noPadding: 'p-0',
  },

  // Footer styling
  footer: {
    default: 'bg-grey-50 px-6 py-4 border-t border-grey-200 flex justify-end gap-3 flex-shrink-0',
    fullWidth: 'bg-grey-50 px-6 py-4 border-t border-grey-200 flex justify-between gap-3 flex-shrink-0',
  },

  // Form element styles
  forms: {
    label: 'block text-sm font-medium text-darkblue-500 mb-2',
    input: 'w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all disabled:bg-grey-100 disabled:cursor-not-allowed',
    select: 'w-full px-4 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white text-sm disabled:bg-grey-100 disabled:cursor-not-allowed transition-all',
    textarea: 'w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none transition-all disabled:bg-grey-100 disabled:cursor-not-allowed',
    required: 'text-pink-500',
  },

  // Info/Alert boxes
  alerts: {
    info: 'bg-brightblue-50 border border-brightblue-200 rounded-lg p-3 text-sm text-grey-700',
    success: 'bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-grey-700',
    warning: 'bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-grey-700',
    error: 'bg-pink-50 border border-pink-200 rounded-lg p-3 text-sm text-grey-700',
  },

  // Typography
  text: {
    heading: 'text-darkblue-500 font-semibold',
    subheading: 'text-darkblue-500 font-medium',
    body: 'text-grey-700',
    muted: 'text-grey-600',
    light: 'text-grey-400',
    emphasis: 'text-brand-600 font-medium',
  },

  // Size mappings
  sizes: {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl',
    full: 'max-w-full mx-4',
  },
};

// Legacy helper functions for backwards compatibility
export const getButtonClass = (variant = 'primary', additionalClasses = '') => {
  return getButtonClasses({ variant, className: additionalClasses });
};

export const getInputClass = (additionalClasses = '') => {
  return `${MODAL_THEME.forms.input} ${additionalClasses}`.trim();
};

export const getAlertClass = (variant = 'info', additionalClasses = '') => {
  return `${MODAL_THEME.alerts[variant]} ${additionalClasses}`.trim();
};